<script lang="ts">
  import { db } from '../../lib/firebase';
  import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
  import type { SyncHistoryEntry } from '../../lib/types';

  type Props = {
    businessId: string | null;
    provider: string;
    providerName: string;
    onClose: () => void;
  };

  let { businessId, provider, providerName, onClose }: Props = $props();

  let entries = $state<SyncHistoryEntry[]>([]);
  let loading = $state(true);

  // Listen to sync history
  $effect(() => {
    if (!businessId || !provider) {
      loading = false;
      return;
    }

    loading = true;

    const q = query(
      collection(db, 'businessdata', businessId, 'integrations', provider, 'syncHistory'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        entries = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SyncHistoryEntry[];
        loading = false;
      },
      (err) => {
        console.error('Error loading sync history:', err);
        loading = false;
      }
    );

    return () => unsubscribe();
  });

  function formatTimestamp(ts: string): string {
    const date = new Date(ts);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const secs = Math.round(ms / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const remaining = secs % 60;
    return `${mins}m ${remaining}s`;
  }

  function getCountsSummary(entry: SyncHistoryEntry): string {
    const parts: string[] = [];
    const c = entry.counts;
    if (entry.syncType === 'orders') {
      if (c.imported) parts.push(`${c.imported} imported`);
      if (c.skipped) parts.push(`${c.skipped} skipped`);
      if (c.errors) parts.push(`${c.errors} failed`);
    } else {
      if (c.created) parts.push(`${c.created} created`);
      if (c.updated) parts.push(`${c.updated} updated`);
      if (c.skipped) parts.push(`${c.skipped} skipped`);
      if (c.errors) parts.push(`${c.errors} failed`);
    }
    return parts.length > 0 ? parts.join(', ') : 'No changes';
  }
</script>

<div class="sync-history-overlay" onclick={onClose} role="presentation">
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div class="sync-history-panel" onclick={(e) => e.stopPropagation()} role="dialog" aria-label="Sync history">
    <div class="panel-header">
      <h3>Sync History &mdash; {providerName}</h3>
      <button class="close-btn" onclick={onClose} aria-label="Close">
        <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <div class="panel-body">
      {#if loading}
        <div class="loading-state">Loading sync history...</div>
      {:else if entries.length === 0}
        <div class="empty-state">
          <p>No sync events recorded yet.</p>
          <p class="empty-hint">Sync history will appear here once {providerName} syncs data with EQUOS.</p>
        </div>
      {:else}
        <table class="history-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Status</th>
              <th>Result</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {#each entries as entry}
              <tr>
                <td class="cell-time">{formatTimestamp(entry.timestamp)}</td>
                <td>
                  <span class="type-badge type-{entry.syncType}">
                    {entry.syncType}
                  </span>
                </td>
                <td>
                  <span class="status-badge status-{entry.status}">
                    {entry.status}
                  </span>
                </td>
                <td class="cell-result">
                  <span>{getCountsSummary(entry)}</span>
                  {#if entry.errors && entry.errors.length > 0}
                    <details class="error-details">
                      <summary>{entry.errors.length} error{entry.errors.length > 1 ? 's' : ''}</summary>
                      <ul>
                        {#each entry.errors as err}
                          <li>{err}</li>
                        {/each}
                      </ul>
                    </details>
                  {/if}
                </td>
                <td class="cell-duration">{formatDuration(entry.durationMs)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
  </div>
</div>

<style>
  .sync-history-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .sync-history-panel {
    background: white;
    border-radius: var(--radius-xl, 12px);
    width: 90%;
    max-width: 750px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--color-border, #e5e7eb);
  }

  .panel-header h3 {
    margin: 0;
    font-size: 1.1rem;
    color: var(--color-text, #333);
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text-tertiary, #6b7280);
    padding: 0.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .close-btn:hover {
    background: var(--color-surface, #f9fafb);
    color: var(--color-text, #333);
  }

  .panel-body {
    overflow-y: auto;
    padding: 1rem 1.5rem 1.5rem;
  }

  .loading-state,
  .empty-state {
    text-align: center;
    padding: 2rem 1rem;
    color: var(--color-text-tertiary, #6b7280);
  }

  .empty-hint {
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }

  /* Table */
  .history-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  .history-table th {
    text-align: left;
    padding: 0.625rem 0.75rem;
    font-weight: 600;
    color: var(--color-text-secondary, #666);
    border-bottom: 2px solid var(--color-border, #e5e7eb);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .history-table td {
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid var(--color-border-light, #f3f4f6);
    vertical-align: top;
  }

  .history-table tbody tr:hover {
    background: var(--color-surface, #f9fafb);
  }

  .cell-time {
    white-space: nowrap;
    color: var(--color-text-secondary, #666);
    font-size: 0.82rem;
  }

  .cell-duration {
    white-space: nowrap;
    color: var(--color-text-tertiary, #6b7280);
    font-size: 0.82rem;
  }

  .cell-result {
    font-size: 0.83rem;
  }

  /* Badges */
  .type-badge {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: capitalize;
  }

  .type-orders {
    background: #ede9fe;
    color: #6d28d9;
  }

  .type-inventory {
    background: #e0f2fe;
    color: #0369a1;
  }

  .status-badge {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: capitalize;
  }

  .status-success {
    background: #d1fae5;
    color: #065f46;
  }

  .status-partial {
    background: #fef3c7;
    color: #92400e;
  }

  .status-failed {
    background: #fee2e2;
    color: #991b1b;
  }

  /* Error details */
  .error-details {
    margin-top: 0.375rem;
  }

  .error-details summary {
    cursor: pointer;
    font-size: 0.78rem;
    color: #991b1b;
  }

  .error-details ul {
    margin: 0.25rem 0 0;
    padding-left: 1.25rem;
    font-size: 0.78rem;
    color: #991b1b;
  }

  .error-details li {
    margin-bottom: 0.125rem;
    word-break: break-word;
  }
</style>
